import React, { useState, useRef } from "react";

// Define FormState type here (copy from App.tsx)
type FormState = {
    card: string;
    device: string;
    name: string;
    type: "primary" | "secondary";
};

type SubscriptionFormProps = {
    form: FormState;
    setForm: React.Dispatch<React.SetStateAction<FormState>>;
    onSubmit: (e: React.FormEvent) => void;
    onCancel: () => void;
    editIndex: number | null;
    onPasteConfig?: (subs: any[], onResult: (success: boolean) => void) => void;
};

const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
    form,
    setForm,
    onSubmit,
    onCancel,
    editIndex,
    onPasteConfig,
}) => {
    const [showPaste, setShowPaste] = useState(false);
    const [pasteValue, setPasteValue] = useState("");
    const [pasteError, setPasteError] = useState("");
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setForm((prev: FormState) => ({ ...prev, [name]: value }));
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (!onPasteConfig) return;
        const text = e.clipboardData.getData("text");
        setPasteValue(text);
        setPasteError("");
        try {
            const parsed = JSON.parse(text);
            if (Array.isArray(parsed)) {
                onPasteConfig(parsed, (success) => {
                    if (!success) {
                        setPasteError("Aucune configuration valide trouvée dans le JSON.");
                    }
                });
            } else {
                setPasteError("Le contenu doit être un tableau JSON.");
            }
        } catch (err) {
            setPasteError("Le contenu n'est pas un JSON valide.");
        }
    };

    const handlePasteButton = () => {
        setShowPaste((v) => !v);
        setPasteError("");
        setTimeout(() => {
            textareaRef.current?.focus();
        }, 100);
    };

    const handlePasteValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setPasteValue(e.target.value);
        setPasteError("");
    };

    const handlePasteSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!onPasteConfig) return;
        try {
            const parsed = JSON.parse(pasteValue);
            if (Array.isArray(parsed)) {
                onPasteConfig(parsed, (success) => {
                    if (!success) {
                        setPasteError("Aucune configuration valide trouvée dans le JSON.");
                    }
                });
            } else {
                setPasteError("Le contenu doit être un tableau JSON.");
            }
        } catch (err) {
            setPasteError("Le contenu n'est pas un JSON valide.");
        }
    };

    return (
        <form
            onSubmit={showPaste ? handlePasteSubmit : onSubmit}
            style={{
                background: "#fff",
                padding: "20px",
                borderRadius: "10px",
                marginBottom: "0px",
                boxShadow: "none",
                minWidth: "320px",
                maxWidth: "400px",
                marginLeft: "auto",
                marginRight: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
            }}
        >
            <h2 style={{ margin: 0 }}>
                {editIndex !== null ? "Edit Subscription" : "Add Subscription"}
            </h2>
            <button
                type="button"
                onClick={handlePasteButton}
                style={{
                    background: "#eee",
                    color: "#6131ad",
                    border: "1px solid #6131ad",
                    borderRadius: "5px",
                    padding: "6px 0",
                    fontWeight: "bold",
                    fontSize: "15px",
                    cursor: "pointer",
                }}
            >
                Importer une configuration
            </button>
            {showPaste ? (
                <>
                    <textarea
                        ref={textareaRef}
                        rows={4}
                        placeholder="Collez ou tapez ici une configuration JSON"
                        style={{ width: "100%", marginBottom: 8, fontFamily: "monospace" }}
                        onPaste={handlePaste}
                        value={pasteValue}
                        onChange={handlePasteValueChange}
                    />
                    {pasteError && (
                        <div style={{ color: "#fe2000", marginBottom: 8 }}>{pasteError}</div>
                    )}
                    <div style={{ display: "flex", gap: "10px" }}>
                        <button
                            type="submit"
                            style={{
                                flex: 1,
                                backgroundColor: "rgb(97, 49, 173)",
                                color: "white",
                                border: "none",
                                padding: "10px 0",
                                borderRadius: "5px",
                                fontWeight: "bold",
                                fontSize: "16px",
                            }}
                        >
                            Importer
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            style={{
                                flex: 1,
                                backgroundColor: "#eee",
                                color: "#333",
                                border: "none",
                                padding: "10px 0",
                                borderRadius: "5px",
                                fontWeight: "bold",
                                fontSize: "16px",
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <label>
                        Card Number
                        <input
                            name="card"
                            value={form.card}
                            onChange={handleChange}
                            required
                            style={{ width: "100%" }}
                        />
                    </label>
                    <label>
                        Name
                        <input
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            required
                            style={{ width: "100%" }}
                        />
                    </label>
                    <label>
                        Type
                        <select
                            name="type"
                            value={form.type}
                            onChange={handleChange}
                            style={{ width: "100%" }}
                        >
                            <option value="primary">Primary</option>
                            <option value="secondary">Secondary</option>
                        </select>
                    </label>
                    {form.type === "primary" && (
                        <label>
                            Device
                            <input
                                name="device"
                                value={form.device}
                                onChange={handleChange}
                                required={form.type === "primary"}
                                style={{ width: "100%" }}
                            />
                        </label>
                    )}
                    <div style={{ display: "flex", gap: "10px" }}>
                        <button
                            type="submit"
                            style={{
                                flex: 1,
                                backgroundColor: "rgb(97, 49, 173)",
                                color: "white",
                                border: "none",
                                padding: "10px 0",
                                borderRadius: "5px",
                                fontWeight: "bold",
                                fontSize: "16px",
                            }}
                        >
                            {editIndex !== null ? "Update" : "Add"}
                        </button>
                        <button
                            type="button"
                            onClick={onCancel}
                            style={{
                                flex: 1,
                                backgroundColor: "#eee",
                                color: "#333",
                                border: "none",
                                padding: "10px 0",
                                borderRadius: "5px",
                                fontWeight: "bold",
                                fontSize: "16px",
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </>
            )}
        </form>
    );
};

export default SubscriptionForm; 